import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfigProvider, Table as AntTable, TableProps } from 'antd'
import cl from 'classnames'
import isBoolean from 'lodash/isBoolean'

import NoData from 'Shared/molecules/NoData'

import Spinner from '../Spinner'

import styles from './styles.module.scss'

interface Props<T> extends TableProps<T> {
	isBlueHeader?: boolean
	noDataParams?: { title?: string; description?: string }
	hasHorizontalScroll?: boolean
	offsetHeader?: number
}

function BasicTable<T extends {}>(props: Props<T>) {
	const {
		isBlueHeader = false,
		onRow,
		pagination = false,
		noDataParams,
		hasHorizontalScroll,
		offsetHeader = 0
	} = props
	const tableElement = document.getElementsByClassName('ant-table')?.[0] as HTMLElement
	const { t } = useTranslation()
	const hasOnRow = !!onRow
	const loading =
		typeof props.loading === 'boolean'
			? {
					spinning: props.loading,
					indicator: (
						<div>
							<Spinner />
						</div>
					)
			  }
			: props.loading

	useEffect(() => {
		// need this to fix ant bug with double scroll
		if (!!props.dataSource?.length && hasHorizontalScroll) {
			tableElement?.scrollBy(0, 1)
		}
	}, [props.dataSource, tableElement, hasHorizontalScroll])

	return (
		<ConfigProvider
			renderEmpty={() => (!props.loading ? <NoData noDataParams={noDataParams} /> : <></>)}
		>
			<AntTable
				rowKey="id"
				{...props}
				pagination={pagination}
				className={cl(
					styles.basicTable,
					props.className,
					styles.tableWithLongTextInCells,
					{ [styles.hasOnRow]: hasOnRow },
					{ [styles.blueHeader]: isBlueHeader }
				)}
				loading={loading}
				locale={{
					triggerAsc: t('core:constants.CLICK_TO_SORT_ASCENDING'),
					triggerDesc: t('core:constants.CLICK_TO_SORT_DESCENDING'),
					cancelSort: t('core:constants.CLICK_TO_CANCEL_SORTING')
				}}
				{...(isBoolean(props.sticky) && {
					sticky: {
						offsetHeader,
						offsetScroll: 0,
						getContainer: () => tableElement
					}
				})}
			/>
		</ConfigProvider>
	)
}

export default BasicTable
